import create from 'zustand';
import shallow from 'zustand/shallow';
import { buildCorrectionsGridRows } from './helpers/buildCorrectionsGridRows';
import { useEffect, useMemo } from 'react';
import { TimeEntryCorrectionData, CorrectionTimeEntryDto, CorrectionDto } from '../../../api/generated/data-contracts';
import { StateSetter } from '../../../types/StateSetter';
import { FunctionOr } from '../../../types/FunctionOr';

// Time entry row - represents a time entry that can have corrections
export type TimeEntryRow = {
    id: string;
    name: string;
    type: 'timeEntry';
    parentId?: string;
    timeEntry: CorrectionTimeEntryDto;
    projectId: string;
    taskId: string;
    userId: string;
    projectName: string;
    taskName: string;
    userName: string;
};

// Correction row - represents a correction to a time entry
export type CorrectionRow = {
    id: string;
    name: string;
    type: 'correction';
    parentId: string;
    correction: CorrectionDto;
};

export type TimeEntryCorrectionRow = TimeEntryRow | CorrectionRow;

export type ExpandedRows = {
    [key: string]: boolean;
};

type CorrectionsStore = {
    rows: TimeEntryCorrectionRow[];
    setRows: StateSetter<TimeEntryCorrectionRow[]>;
    timeEntries: Record<string, CorrectionTimeEntryDto>;
    setTimeEntries: StateSetter<Record<string, CorrectionTimeEntryDto>>;
    expandedRows: ExpandedRows;
    setExpandedRows: StateSetter<ExpandedRows>;
    setGridExpandedRow: (rowId: string, expand: boolean) => void;
    selectedProjectIds: string[];
    setSelectedProjectIds: StateSetter<string[]>;
    selectedUserIds: string[];
    setSelectedUserIds: StateSetter<string[]>;
    selectedTaskIds: string[];
    setSelectedTaskIds: StateSetter<string[]>;
    projects: Record<string, any>;
    users: Record<string, any>;
    tasks: Record<string, any>;
    initState: (data: TimeEntryCorrectionData) => void;
    addNewCorrection: (timeEntryId: string, correction: CorrectionDto) => void;
    cleanup: () => void;
};

const unwrap = <T>(valueOrUpdater: FunctionOr<T>, currentState: T) => {
    if (typeof valueOrUpdater === 'function') {
        return (valueOrUpdater as (v: T) => T)(currentState);
    }
    return valueOrUpdater;
};

export const useCorrectionsStore = create<CorrectionsStore>((set, get) => ({
    rows: [],
    setRows: valueOrUpdater => {
        set(store => ({ ...store, rows: unwrap(valueOrUpdater, store.rows) }));
    },
    timeEntries: {},
    setTimeEntries: valueOrUpdater => {
        set(store => ({ ...store, timeEntries: unwrap(valueOrUpdater, store.timeEntries) }));
    },
    expandedRows: {},
    setExpandedRows: valueOrUpdater =>
        set(store => ({
            ...store,
            expandedRows: unwrap(valueOrUpdater, store.expandedRows),
        })),
    setGridExpandedRow: (id, expand) => {
        set(store => {
            if (expand) {
                const parentRows = getParentRows(id, store.rows);
                const subrowFamily = getFamilyOfSubrows(id, store.rows);
                return {
                    ...store,
                    expandedRows: [...parentRows, ...subrowFamily].reduce(
                        (newState, row) => {
                            newState[row.id] = true;
                            return newState;
                        },
                        { ...store.expandedRows, [id]: true } as ExpandedRows,
                    ),
                };
            }
            const subrowFamily = getFamilyOfSubrows(id, store.rows);
            return {
                ...store,
                expandedRows: subrowFamily.reduce(
                    (newState, row) => {
                        newState[row.id] = false;
                        return newState;
                    },
                    { ...store.expandedRows, [id]: false } as ExpandedRows,
                ),
            };
        });
    },
    selectedProjectIds: [],
    setSelectedProjectIds: valueOrUpdater => {
        set(store => {
            const nextState = unwrap(valueOrUpdater, store.selectedProjectIds);
            const filteredTimeEntries = Object.fromEntries(
                Object.entries(store.timeEntries).filter(([id, timeEntry]) => nextState.includes(timeEntry.projectId)),
            );
            return {
                ...store,
                selectedProjectIds: nextState,
                rows: buildCorrectionsGridRows({
                    timeEntries: filteredTimeEntries,
                    projects: store.projects,
                    tasks: store.tasks,
                    users: store.users,
                }),
            };
        });
    },
    selectedUserIds: [],
    setSelectedUserIds: valueOrUpdater => {
        set(store => {
            const nextState = unwrap(valueOrUpdater, store.selectedUserIds);
            const filteredTimeEntries = Object.fromEntries(Object.entries(store.timeEntries).filter(([id, timeEntry]) => nextState.includes(timeEntry.userId)));
            return {
                ...store,
                selectedUserIds: nextState,
                rows: buildCorrectionsGridRows({
                    timeEntries: filteredTimeEntries,
                    projects: store.projects,
                    tasks: store.tasks,
                    users: store.users,
                }),
            };
        });
    },
    selectedTaskIds: [],
    setSelectedTaskIds: valueOrUpdater => {
        set(store => {
            const nextState = unwrap(valueOrUpdater, store.selectedTaskIds);
            const filteredTimeEntries = Object.fromEntries(Object.entries(store.timeEntries).filter(([id, timeEntry]) => nextState.includes(timeEntry.taskId)));
            return {
                ...store,
                selectedTaskIds: nextState,
                rows: buildCorrectionsGridRows({
                    timeEntries: filteredTimeEntries,
                    projects: store.projects,
                    tasks: store.tasks,
                    users: store.users,
                }),
            };
        });
    },
    projects: {},
    users: {},
    tasks: {},
    initState: data => {
        const rows = buildCorrectionsGridRows(data);
        const uniqueProjectIds = Object.keys(data.projects);
        const uniqueUserIds = Object.keys(data.users);
        const uniqueTaskIds = Object.keys(data.tasks);

        set(store => {
            // Preserve existing expanded state for rows that still exist
            const preservedExpandedRows = { ...store.expandedRows };
            const newExpandedRows = rows.reduce((acc, row) => {
                // Keep existing expanded state if the row existed before, otherwise default to false
                acc[row.id] = preservedExpandedRows[row.id] ?? false;
                return acc;
            }, {} as ExpandedRows);

            return {
                ...store,
                rows,
                timeEntries: data.timeEntries,
                projects: data.projects,
                users: data.users,
                tasks: data.tasks,
                expandedRows: newExpandedRows,
                selectedProjectIds: uniqueProjectIds,
                selectedUserIds: uniqueUserIds,
                selectedTaskIds: uniqueTaskIds,
            };
        });
    },
    addNewCorrection: (timeEntryId: string, correction: CorrectionDto) => {
        set(store => {
            // Create the new correction row
            const correctionRow: CorrectionRow = {
                id: correction.id,
                name: `Correction: ${correction.comment.substring(0, 50)}${correction.comment.length > 50 ? '...' : ''}`,
                type: 'correction',
                parentId: timeEntryId,
                correction,
            };

            // Add the correction row to the rows array
            const newRows = [...store.rows, correctionRow];

            // Ensure the parent time entry is expanded to show the new correction
            const newExpandedRows = { ...store.expandedRows, [timeEntryId]: true };

            return {
                ...store,
                rows: newRows,
                expandedRows: newExpandedRows,
            };
        });
    },
    cleanup: () => {
        set({
            rows: [],
            timeEntries: {},
            projects: {},
            users: {},
            tasks: {},
            expandedRows: {},
            selectedProjectIds: [],
            selectedUserIds: [],
            selectedTaskIds: [],
        });
    },
}));

export const getParentRows = (rowId: string, rows: TimeEntryCorrectionRow[]) => {
    const tree: TimeEntryCorrectionRow[] = [];
    let currentAncestor = rows.find(row => row.id === rowId);
    while (currentAncestor) {
        currentAncestor = rows.find(row => row.id === currentAncestor.parentId);
        if (currentAncestor) {
            tree.push(currentAncestor);
        }
    }
    return tree;
};

export const getFamilyOfSubrows = (rowId: string, rows: TimeEntryCorrectionRow[]) => {
    const subrowFamily: TimeEntryCorrectionRow[] = [];
    const recurse = (rowId: string) => {
        const subrows = getImmediateSubrows(rowId, rows);
        subrows.forEach(subrow => {
            subrowFamily.push(subrow);
            recurse(subrow.id);
        });
    };
    recurse(rowId);
    return subrowFamily;
};

const isRootRow = (row: TimeEntryCorrectionRow) => {
    return !row.parentId;
};

export const getImmediateSubrows = (rowId: string, rows: TimeEntryCorrectionRow[]) => {
    return rows.filter(row => row.parentId === rowId);
};

export const isTimeEntryRow = (row: TimeEntryCorrectionRow): row is TimeEntryRow => row.type === 'timeEntry';

export const isCorrectionRow = (row: TimeEntryCorrectionRow): row is CorrectionRow => row.type === 'correction';

export const useCorrectionsGridRenderableRows = (rows: TimeEntryCorrectionRow[]) => {
    const expandedRows = useCorrectionsStore(store => store.expandedRows, shallow);
    return useMemo(() => {
        return rows.filter(row => {
            return isRootRow(row) || expandedRows[row.parentId];
        });
    }, [expandedRows, rows]);
};

export const useInitializeCorrectionsStore = (data?: TimeEntryCorrectionData) => {
    const { initState, cleanup, rows } = useCorrectionsStore(
        store => ({
            initState: store.initState,
            cleanup: store.cleanup,
            rows: store.rows,
        }),
        shallow,
    );

    useEffect(() => {
        if (data) {
            initState(data);
        }
    }, [data, initState]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, [cleanup]);

    return rows;
};

export const useGridExpandedRowState = (id: string) =>
    useCorrectionsStore(
        store =>
            [
                store.expandedRows[id],
                (valueOrUpdater: FunctionOr<boolean>) => {
                    const nextState = unwrap(valueOrUpdater, store.expandedRows[id]);
                    const subrows = getFamilyOfSubrows(id, store.rows);
                    store.setExpandedRows(lastState => {
                        return subrows.reduce(
                            (next, row) => {
                                next[row.id] = nextState;
                                return next;
                            },
                            { ...lastState, [id]: nextState } as ExpandedRows,
                        );
                    });
                },
            ] as const,
        shallow,
    );
